/**
 * Exmaple implementation:
 *
 *     <DocumentsTable
 *       data={data}
 *       data-qa-ref="signature-requests-table"
 *       defaultPage={2}
 *       onPageChange={(newPage) => doSomething(newPage)}
 *       isLoading={isLoading}
 *       columnNames={[
 *         'Title',
 *         'Status',
 *       ]}
 *       columnData={[
 *         (doc) => doc.title,
 *         (doc) => doc.status,
 *       ]} />
 *
 * See also hellospa/components/signature-requests/signature-requests-table.tsx.
 */

import React from 'react';
import Table from 'hellospa/foundation/tables/table';

/**
 * The baseline for how data is structured.
 *
 *   type SignatureRequestShape = TableDataShape<{
 *     sender: string,
 *   }};
 */
export type TableDataShape<T> = T & {
  guid: string,
  disabled?: boolean,
}

/**
 * The baseline for how response data is structured. You
 * can extend this for your specific use case by creating
 * a new type, like this:
 *
 *   type SignatureRequestDataShape = TableDataContainer<
 *     SignatureRequestShape
 *   >;
 */
export interface TableDataContainer<T extends TableDataShape<unknown>> {
  currentPage: number,
  numPages: number,
  resultsCount: number,
  numResults?: number,
  numResultsTotal?: number,
  pageResults: T[],
}

export type DocumentsTablePropTypes<T> = React.BaseHTMLAttributes<HTMLTableElement> & {
  defaultPage?: number,
  isLoading?: boolean,
  showPagination?: boolean
  onPageChange: (page: number) => void,
  columnNames: Array<
    | string
    | JSX.Element
    | ((data: TableDataContainer<TableDataShape<T>> | null) => JSX.Element)
  >
  columnData: Array<string | JSX.Element | ((doc: TableDataShape<T>) => (JSX.Element | null))>,
  currentPage: number,
  onSelectRow: (key: string | null) => void,
  activeKey: string | null,
  disabled?: boolean,
  data: TableDataContainer<TableDataShape<T>> | null
};

export function DocumentsTable<T>({
  onPageChange,
  columnNames,
  columnData,
  isLoading = false,
  showPagination = true,
  currentPage,
  onSelectRow,
  activeKey,
  data,

  // add'l props will be applied to the root <table> element
  ...props
}: DocumentsTablePropTypes<T>) {
  // const data = React.useContext<BaseDataShape<T> | null>(DocumentsTableDataContext);

  const handlePageChange = React.useCallback((page) => {
    onPageChange(page);
  }, [onPageChange]);

  return (
    <>
      <Table
        {...props}
        currentPage={currentPage}
        numPages={data ? data.numPages : 1}
        onPageChange={handlePageChange}
        allowSelect={!isLoading && !!data}
        showPagination={!isLoading && !!data && showPagination}
        isLoading={isLoading}
        onSelectRow={onSelectRow}
        activeKey={activeKey}
      >
        <Table.Head>
          <Table.Row>
            {columnNames.map((render, i) => (
              <Table.Heading
                key={`col-${i}`}

                // The first column (typically the "name" column) will
                // fill all remaining space in the table.
                {...(i === 0 ? { width: '99%' } : {})}
              >
                {typeof render === 'function' ? render(data) : render}
              </Table.Heading>
            ))}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {(data && !isLoading) && (
            data.pageResults.map((doc) => (
              <Table.Row key={doc.guid} eventKey={doc.guid} disabled={doc.disabled}>
                {columnData.map((render, i) => (
                  <Table.Data key={`col-${i}`}>
                    {typeof render === 'function' ? render(doc) : render}
                  </Table.Data>
                ))}
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>
    </>
  );
}
